import { format } from "date-fns/format";
import { enUS, ja } from "date-fns/locale";
import React from "react";
import { useTranslation } from "react-i18next";
import ArrowSeparator from "@/assets/icons/arrowSeparator.svg?react";
import ArrowSeparatorS from "@/assets/icons/arrowSeparatorS.svg?react";
import { Badge } from "@/components/atomic/Badge";
import { Card, CardContent } from "@/components/atomic/Card";
import { cn } from "@/lib/utils";
import { Voc } from "@/models/Voc";

interface Props {
  className?: string;
  voc: Voc;
  visibleAttributeNames?: string[];
}

const VocCard: React.FC<Props> = ({ className = "", voc, visibleAttributeNames }) => {
  const { t, i18n } = useTranslation("voiceek_ui");

  // 言語に応じたロケールを選択
  const locale = i18n.language === "ja" ? ja : enUS;

  const metadata = [
    {
      label: t("analysis.voc.date"),
      value: format(voc.recordedAt, "yyyy/MM/dd", { locale }),
    },
    { label: t("analysis.voc.id"), value: voc.id },
  ];

  // extra_attributes をメタデータに追加
  const extraAttributesMetadata = voc.extraAttributes
    ? Object.entries(voc.extraAttributes)
      .filter(([key]) => !visibleAttributeNames || visibleAttributeNames.includes(key))
      .map(([key, value]) => ({ label: key, value: String(value) }))
    : [];

  const categories = voc.categoryPath.split("/").slice(1, -1);

  return (
    <div className={cn("flex flex-col items-start relative bg-white ", className)}>
      <Card className="p-5 border border-solid border-[#B7B7B7] rounded-[10px] shadow-none w-full">
        <CardContent className="flex flex-col gap-[10px] p-0">
          <div className="flex flex-wrap items-start gap-[10px_10px]">
            {/* Metadata section */}
            <div className="w-full flex flex-wrap items-center gap-3">
              {metadata.map((item) => (
                <div key={item.label} className="inline-flex items-center gap-[5px]">
                  <Badge className="px-2.5 py-0 h-[18px] bg-[#293d72] rounded-[5px] text-[10px] font-bold">
                    {item.label}
                  </Badge>
                  <span className="text-[10px]">
                    {item.value}
                  </span>
                </div>
              ))}

              {/* Categories section */}
              <div className="inline">
                <Badge className="shrink-0 mr-1.5 px-2.5 py-0 h-[18px] bg-[#293d72] rounded-[5px] text-[10px] font-bold">
                  {t("analysis.voc.category")}
                </Badge>
                <div className="relative inline align-middle px-[5px] pb-[1px] leading-[24px] rounded-[5px_0px_0px_5px] bg-[#ebebeb]">
                  {categories.map((category, index) => (
                    <React.Fragment key={category}>
                      {/* 最後のカテゴリは太字に */}
                      <span
                        className={cn(
                          "align-middle ml-1.5 text-[10px] text-black text-ellipsis whitespace-pre-line",
                          index === categories.length - 1 ? "font-bold" : "font-normal",
                        )}
                        title={category}
                      >
                        {category}
                      </span>
                      {index < categories.length - 1 && <ArrowSeparatorS className="inline mt-[-1px] ml-1.5" />}
                    </React.Fragment>
                  ))}
                </div>
                <ArrowSeparator className="inline" />
              </div>
            </div>

            {/* Extra Attributes section - 改行して別の行に表示 */}
            {extraAttributesMetadata.length > 0 && (
              <div className="w-full flex flex-wrap items-center gap-3">
                {extraAttributesMetadata.map((item) => (
                  <div key={item.label} className="inline-flex items-center gap-[5px]">
                    <Badge className="px-2.5 py-0 h-[18px] bg-[#293d72] rounded-[5px] text-[10px] font-bold">
                      {item.label}
                    </Badge>
                    <span className="text-[10px]">
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* VoC content */}
          <div className="w-full text-[#333333] leading-[1.8]">
            {voc.vocText}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default VocCard;
